<template>
  <a-drawer
    title="设备 Hms 信息"
    placement="right"
    v-model:visible="sVisible"
    @update:visible="onVisibleChange"
    :destroyOnClose="true"
    :width="800">
    <div class="flex-row flex-align-center">
      <div style="width: 240px;">
        <a-range-picker
          v-model:value="time"
          format="YYYY-MM-DD"
          :placeholder="['开始时间', '结束时间']"
          @change="onTimeChange"/>
      </div>
      <div class="ml5">
        <a-select
          style="width: 150px"
          v-model:value="param.level"
          @select="onLevelSelect">
          <a-select-option
            v-for="item in levels"
            :key="item.label"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </div>
      <div class="ml5">
        <a-select
          v-model:value="param.domain"
          :disabled="!param.children_sn || !param.device_sn"
          style="width: 150px"
          @select="onDeviceTypeSelect">
          <a-select-option
            v-for="item in deviceTypes"
            :key="item.label"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </div>
      <div class="ml5">
        <a-input-search
          v-model:value="param.message"
          placeholder="输入搜索消息"
          style="width: 200px"
          @search="getHms"/>
      </div>
    </div>
    <div>
      <a-table :columns="hmsColumns"  :scroll="{ x: '100%', y: 600 }" :data-source="hmsData.data" :pagination="hmsPaginationProp" @change="refreshHmsData" row-key="hms_id"
        :rowClassName="rowClassName" :loading="loading">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'time'">
            <div>{{ record.create_time }}</div>
            <div :style="record.update_time ? '' : record.level === EHmsLevel.CAUTION ? 'color: orange;' :
              record.level === EHmsLevel.WARN ? 'color: red;' : 'color: #28d445;'">{{ record.update_time ?? '正在发生...' }}
            </div>
          </template>
          <template v-if="column.key === 'level'">
            <div class="flex-row flex-align-center">
              <div :class="record.level === EHmsLevel.CAUTION ? 'caution': record.level === EHmsLevel.WARN ? 'warn' : 'notice'" style="width: 10px; height: 10px; border-radius: 50%;"></div>
              <div style="margin-left: 3px;">{{ EHmsLevelMap[record.level as EHmsLevel] }}</div>
            </div>
          </template>
          <template v-if="column.key === 'message'">
            <a-tooltip :title="record.message_zh">
              <div >{{ record.message_zh }}</div>
            </a-tooltip>
          </template>
          <template v-if="column.key === 'domain'">
            <a-tooltip :title="EDeviceTypeNameMap[record.domain as EDeviceTypeName]">
              <div>{{ EDeviceTypeNameMap[record.domain as EDeviceTypeName] }}</div>
          </a-tooltip>
          </template>
        </template>
      </a-table>
    </div>
  </a-drawer>
</template>

<!-- 暂时只抽取该组件 -->
<script lang="ts" setup>
import { reactive, ref, defineProps, defineEmits, watch } from 'vue'
import { getDeviceHms, HmsQueryBody } from '/@/api/manage'
import { ColumnProps, TableState } from 'ant-design-vue/lib/table/interface'
import { Device, DeviceHms } from '/@/types/device'
import { IPage } from '/@/api/http/type'
import { EDeviceTypeName, EHmsLevel, EDeviceTypeNameMap, EHmsLevelMap, ELocalStorageKey } from '/@/types'
import dayjs, { Dayjs } from 'dayjs'

const props = defineProps<{
  visible: boolean,
  device: null | Device,
}>()
const emit = defineEmits(['update:visible', 'ok', 'cancel'])

const workspaceId: string = localStorage.getItem(ELocalStorageKey.WorkspaceId) || ''
// 健康状态
const sVisible = ref(false)

watch(props, () => {
  sVisible.value = props.visible
  // 显示弹框时，获取设备hms信息
  if (props.visible) {
    showHms()
  }
})

function onVisibleChange (sVisible: boolean) {
  setVisible(sVisible)
}

function setVisible (v: boolean, e?: Event) {
  sVisible.value = v
  emit('update:visible', v, e)
}

const loading = ref(false)

const hmsColumns: ColumnProps[] = [
  { title: '告警开始 | 结束时间', dataIndex: 'create_time', key: 'time', width: '25%', className: 'titleStyle' },
  { title: '级别', dataIndex: 'level', key: 'level', width: '120px', className: 'titleStyle' },
  { title: '设备', dataIndex: 'domain', key: 'domain', width: '12%', className: 'titleStyle' },
  { title: '错误代码', dataIndex: 'key', key: 'code', width: '20%', className: 'titleStyle', ellipsis: true },
  { title: 'Hms 消息', dataIndex: 'message_zh', key: 'message', className: 'titleStyle', ellipsis: true },
]

interface DeviceHmsData {
  data: DeviceHms[]
}

const hmsData = reactive<DeviceHmsData>({
  data: []
})

type Pagination = TableState['pagination']

const hmsPaginationProp = reactive({
  pageSizeOptions: ['20', '50', '100'],
  showQuickJumper: true,
  showSizeChanger: true,
  pageSize: 50,
  current: 1,
  total: 0
})

// 获取分页信息
function getPaginationBody () {
  return {
    page: hmsPaginationProp.current,
    page_size: hmsPaginationProp.pageSize
  } as IPage
}

function showHms () {
  const dock = props.device
  if (!dock) return
  if (dock.domain === EDeviceTypeName.Dock) {
    getDeviceHmsBySn(dock.device_sn, dock.children?.[0].device_sn ?? '')
  }
  if (dock.domain === EDeviceTypeName.Aircraft) {
    param.domain = EDeviceTypeName.Aircraft
    getDeviceHmsBySn('', dock.device_sn)
  }
}

function refreshHmsData (page: Pagination) {
  hmsPaginationProp.current = page?.current!
  hmsPaginationProp.pageSize = page?.pageSize!
  getHms()
}

const defaultStart = dayjs().subtract(7, 'day').startOf('day')
const defaultEnd = dayjs().endOf('day')

const param = reactive<HmsQueryBody>({
  sns: [],
  device_sn: '',
  children_sn: '',
  language: 'en',
  begin_time: defaultStart.format('YYYY-MM-DD') + ' 00:00:00',
  end_time: defaultEnd.format('YYYY-MM-DD') + ' 23:59:59',
  domain: -1,
  level: '',
  message: ''
})

const levels = [
  {
    label: '请选择级别',
    value: ''
  }, {
    label: EHmsLevelMap[EHmsLevel.NOTICE],
    value: EHmsLevel.NOTICE
  }, {
    label: EHmsLevelMap[EHmsLevel.CAUTION],
    value: EHmsLevel.CAUTION
  }, {
    label: EHmsLevelMap[EHmsLevel.WARN],
    value: EHmsLevel.WARN
  }
]

const deviceTypes = [
  {
    label: '请选择设备类型',
    value: -1
  }, {
    label: EDeviceTypeNameMap[EDeviceTypeName.Aircraft],
    value: EDeviceTypeName.Aircraft
  }, {
    label: EDeviceTypeNameMap[EDeviceTypeName.Dock],
    value: EDeviceTypeName.Dock
  }
]

const rowClassName = (record: any, index: number) => {
  const className = []
  if ((index & 1) === 0) {
    className.push('table-striped')
  }
  if (record.domain !== EDeviceTypeName.Dock) {
    className.push('child-row')
  }
  return className.toString().replaceAll(',', ' ')
}

const time = ref([dayjs(param.begin_time), dayjs(param.end_time)])

function getHms () {
  loading.value = true
  getDeviceHms(param, workspaceId, getPaginationBody())
    .then(res => {
      hmsPaginationProp.total = res.data.pagination.total
      hmsPaginationProp.current = res.data.pagination.page
      hmsData.data = res.data.list
      hmsData.data.forEach(hms => {
        hms.domain = hms.sn === param.children_sn ? EDeviceTypeName.Aircraft : EDeviceTypeName.Dock
      })
      loading.value = false
    }).catch(_err => {
      loading.value = false
    })
}

function getDeviceHmsBySn (sn: string, childSn: string) {
  param.device_sn = sn
  param.children_sn = childSn
  param.sns = [param.device_sn, param.children_sn]
  getHms()
}

function onTimeChange (newTime: [Dayjs, Dayjs]) {
  const [start, end] = newTime
  param.begin_time = start.format('YYYY-MM-DD') + ' 00:00:00'
  param.end_time = end.format('YYYY-MM-DD') + ' 23:59:59'
  getHms()
}

function onDeviceTypeSelect (val: number) {
  param.sns = [param.device_sn, param.children_sn]
  if (val === EDeviceTypeName.Dock) {
    param.sns = [param.device_sn, '']
  }
  if (val === EDeviceTypeName.Aircraft) {
    param.sns = ['', param.children_sn]
  }
  getHms()
}

function onLevelSelect (val: number) {
  param.level = val
  getHms()
}
</script>
